import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { green, grey } from "@mui/material/colors";
import { memo, useCallback, useMemo, useState } from "react";
import {
  ChatMessageWithParts,
  ContentType,
  UserSelectionPromptData,
} from "src/views/assistant/types";

import { CheckCircleOutline as CheckIcon } from "@mui/icons-material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  deliverToolOutcome,
  useDeliveryWatcher,
} from "src/reusable/Chat/ChatStreaming/delivery";
import { useChatStreamStore } from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";
import { NucToast } from "src/reusable/components/Toast";
import { useChatContext } from "../../context/ChatContext";
import PromptMarkdown from "./PromptMarkdown";
import { resolveOutcome, useStaleUnanswered } from "./resolveOutcome";
import {
  getInnerResult,
  parseToolResponseText,
  patchToolResponseOutcome,
} from "./toolStatusUtils";

interface UserSelectionPromptProps {
  /** Raw text containing JSON prompt data */
  text: string;
  /** Whether the chat is still streaming */
  isChatConnected: boolean;
  /** Message ID this prompt belongs to (for updating the dialogue) */
  messageId?: string;
  /** Index of this content part within the message */
  partIndex?: number;
}

// Validates user_selection has required fields. Module-scoped: it's a pure
// type guard, and callbacks that use it must not need it as a dependency.
const isValidUserSelection = (
  sel: unknown,
): sel is { value: string; label: string } => {
  return (
    sel !== null &&
    typeof sel === "object" &&
    "value" in sel &&
    "label" in sel &&
    typeof (sel as { value: unknown }).value === "string" &&
    typeof (sel as { label: unknown }).label === "string" &&
    (sel as { value: string }).value.length > 0
  );
};

// Questionnaire semantics: when the same parked turn asked several
// interactive prompts, the backend holds the turn until every one is
// answered — so the typing indicator must not light on a non-final answer
// (nothing will stream yet).
const otherPromptsStillUnanswered = (
  dialogue: ChatMessageWithParts[],
  self: { messageId?: string; partIndex?: number; promptId?: string },
): boolean => {
  for (const msg of dialogue) {
    const parts = msg.contentParts || [];
    for (let idx = 0; idx < parts.length; idx++) {
      const part = parts[idx];
      if (part.type !== "tool_response") continue;
      // Positional self-exclusion: prompt_id can be absent on this very
      // prompt, and matching on it alone would then count self as an
      // unanswered sibling and suppress the turn-active state.
      if (msg.id === self.messageId && idx === self.partIndex) continue;
      const inner = getInnerResult(parseToolResponseText(part.text || ""));
      if (!inner || typeof inner.type !== "string") continue;
      if (
        inner.type !== "user_selection_prompt" &&
        inner.type !== "user_file_upload_prompt"
      )
        continue;
      if (self.promptId && inner.prompt_id === self.promptId) continue;
      const answered =
        isValidUserSelection(inner.user_selection) ||
        inner.user_upload != null ||
        inner.user_declined === true;
      if (!answered) return true;
    }
  }
  return false;
};

/**
 * UserSelectionPrompt Component
 *
 * Renders an interactive selection form based on tool response data.
 * Supports dropdown, radio buttons, button group, and checkbox selections.
 */
const UserSelectionPrompt = memo(
  ({
    text,
    isChatConnected,
    messageId,
    partIndex,
  }: UserSelectionPromptProps) => {
    const { dialogueId } = useChatContext();
    // Host chat's store — useAtomAssistChat here used to clobber the host
    // surface's stream config with this card's minimal one.
    const streamStore = useChatStreamStore();
    const trackDelivery = useDeliveryWatcher();

    const [selectedValue, setSelectedValue] = useState<string>("");
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [customValue, setCustomValue] = useState<string>("");
    const [isCustomMode, setIsCustomMode] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submittedSelection, setSubmittedSelection] = useState<{
      value: string;
      label: string;
    } | null>(null);

    // Parse the prompt data and check for existing selection (from history)
    const { promptData, existingSelection } = useMemo<{
      promptData: UserSelectionPromptData | null;
      existingSelection: { value: string; label: string } | null;
    }>(() => {
      try {
        // Extract JSON from tool_response tags if present
        let jsonText = text;
        if (text.includes("<tool_response>")) {
          jsonText = text
            .replace(/^<tool_response>\s*/, "")
            .replace(/\s*<\/tool_response>$/, "");
        }
        const parsed = JSON.parse(jsonText);

        // Check for user_selection in the parsed data (indicates already submitted)
        // Must have both value and label fields to be valid
        const rawUserSelection = parsed.user_selection;
        const userSelection = isValidUserSelection(rawUserSelection)
          ? rawUserSelection
          : null;

        // Direct type check (if tool returns unwrapped)
        if (parsed.type === "user_selection_prompt") {
          return {
            promptData: parsed as UserSelectionPromptData,
            existingSelection: userSelection,
          };
        }

        // Check nested result (standard ToolResult wrapper)
        if (parsed.message_type === "tool_result" && parsed.result) {
          const innerResult =
            typeof parsed.result === "string"
              ? JSON.parse(parsed.result)
              : parsed.result;
          if (innerResult?.type === "user_selection_prompt") {
            const innerUserSelection = innerResult.user_selection;
            return {
              promptData: innerResult as UserSelectionPromptData,
              existingSelection:
                userSelection ||
                (isValidUserSelection(innerUserSelection)
                  ? innerUserSelection
                  : null),
            };
          }
        }

        return { promptData: null, existingSelection: null };
      } catch {
        return { promptData: null, existingSelection: null };
      }
    }, [text]);

    // If there's an existing selection from history, show as already submitted
    const hasExistingSelection = existingSelection !== null;

    const handleSingleSelect = useCallback((value: string) => {
      setSelectedValue(value);
      setIsCustomMode(false);
    }, []);

    const handleCheckboxChange = useCallback((value: string) => {
      setSelectedValues((prev) =>
        prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value],
      );
    }, []);

    const handleCustomToggle = useCallback(() => {
      setIsCustomMode(true);
      setSelectedValue("");
    }, []);

    // Persist the outcome into the part text so it survives a re-render;
    // passing `undefined` strips it again when delivery fails. True only when
    // the part was actually rewritten: the flag selects continueConversation
    // in the delivery cascade, which must not resume a turn whose dialogue
    // never carried the answer.
    const patchOutcome = useCallback(
      (
        userSelection:
          | { value: string; label: string; submitted_at: string }
          | undefined,
      ) => {
        if (
          messageId === undefined ||
          partIndex === undefined ||
          streamStore === null
        ) {
          return false;
        }
        let patched = false;
        streamStore
          .getState()
          .setDialogue((prevDialogue: ChatMessageWithParts[]) =>
            prevDialogue.map((msg) => {
              if (msg.id !== messageId) return msg;

              const updatedParts = msg.contentParts.map((part, idx) => {
                if (idx !== partIndex) return part;
                if (part.type !== ContentType.TOOL_RESPONSE) return part;
                const text = patchToolResponseOutcome(part.text, {
                  user_selection: userSelection,
                });
                if (text === null) return part;
                patched = true;
                return { ...part, text };
              });

              return { ...msg, contentParts: updatedParts };
            }),
          );
        return patched;
      },
      [messageId, partIndex, streamStore],
    );

    // Submit the selection. Accepts optional direct value/label for auto-submit
    // (buttons component type submits on click without requiring Continue).
    const handleSubmit = useCallback(
      async (directValue?: string, directLabel?: string) => {
        if (!promptData) return;

        let responseValue: string;
        let responseLabel: string;
        if (directValue !== undefined) {
          responseValue = directValue;
          responseLabel = directLabel || directValue;
        } else if (isCustomMode && customValue.trim()) {
          responseValue = customValue.trim();
          responseLabel = responseValue;
        } else if (promptData.component_type === "checkbox") {
          responseValue = selectedValues.join(",");
          responseLabel = promptData.options
            .filter((opt) => selectedValues.includes(opt.value))
            .map((opt) => opt.label)
            .join(", ");
        } else if (selectedValue === "__none__") {
          responseValue = "none";
          responseLabel = "None of these";
        } else {
          responseValue = selectedValue;
          const selectedOption = promptData.options.find(
            (opt) => opt.value === selectedValue,
          );
          responseLabel = selectedOption?.label || selectedValue;
        }
        if (!responseValue) return;

        // No store, no delivery path: leave the prompt answerable rather
        // than showing a submitted state for an answer that went nowhere.
        if (streamStore === null) {
          console.error("[UserSelectionPrompt:no-host-store]", { dialogueId });
          return;
        }

        setIsSubmitted(true);
        setSubmittedSelection({ value: responseValue, label: responseLabel });
        const resumesTurn = !otherPromptsStillUnanswered(
          streamStore.getState().dialogue,
          { messageId, partIndex, promptId: promptData.prompt_id },
        );
        const patched = patchOutcome({
          value: responseValue,
          label: responseLabel,
          submitted_at: new Date().toISOString(),
        });

        // Back to answerable. The local patch goes too, or the parsed part
        // text would keep the card in the submitted state.
        const revert = (message: string) => {
          setIsSubmitted(false);
          setSubmittedSelection(null);
          if (patched) patchOutcome(undefined);
          NucToast({ message, type: "error" });
        };

        // The injection posts `selection` so the backend annotates the tool
        // response (outcome survives reload); old backends drop the unknown
        // field and deliver `content` as a plain user message.
        const result = await deliverToolOutcome(streamStore, {
          dialogueId,
          promptId: promptData.prompt_id,
          injectionBody: {
            selection: {
              prompt_id: promptData.prompt_id,
              value: responseValue,
              label: responseLabel,
            },
          },
          fallbackMessage:
            directValue !== undefined ? responseValue : responseLabel,
          patched,
          resumesTurn,
        });
        if (result.dispatch === "rejected") {
          // Every delivery path failed: the answer never reached the backend.
          revert("Your answer could not be sent. Please try again.");
          return;
        }
        // The stream proves delivery. A demotion means the answer is lost on
        // a parked turn: a reload would show the prompt unanswered, so the
        // card must not keep the checkmark.
        void trackDelivery(result).then((verdict) => {
          if (verdict !== "undelivered") return;
          console.error("[UserSelectionPrompt:undelivered]", {
            dialogueId,
            promptId: promptData.prompt_id,
          });
          revert(
            "Your answer may not have reached the assistant. Please try again.",
          );
        });
      },
      [
        promptData,
        isCustomMode,
        customValue,
        selectedValue,
        selectedValues,
        messageId,
        partIndex,
        streamStore,
        dialogueId,
        patchOutcome,
        trackDelivery,
      ],
    );

    // Check if submit should be enabled
    const canSubmit = useMemo(() => {
      if (!promptData) return false;
      if (isCustomMode) return customValue.trim().length > 0;
      if (promptData.component_type === "checkbox") {
        return selectedValues.length > 0;
      }
      return selectedValue !== "";
    }, [promptData, isCustomMode, customValue, selectedValue, selectedValues]);

    // Check if content is complete (has closing tag, raw JSON, or stream ended)
    const isContentComplete = useMemo(() => {
      const normalizedText = text.trimStart();
      if (normalizedText.includes("</tool_response>")) return true;
      if (!normalizedText.startsWith("<tool_response>")) return true;
      return !isChatConnected;
    }, [text, isChatConnected]);

    // Collapse the form if the user already answered in plain text (later User message, no outcome).
    const isStaleUnanswered = useStaleUnanswered(messageId);

    // Don't render if invalid data or content is still incomplete
    if (!promptData || !isContentComplete) {
      return null;
    }

    if (isStaleUnanswered && !isSubmitted && !hasExistingSelection) {
      return (
        <Box
          sx={{
            mt: 0,
            mb: 1,
            borderRadius: "0 0 6px 6px",
            border: `1px solid ${grey[300]}`,
            borderTop: "none",
            bgcolor: grey[100],
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.6,
              borderTop: `1px solid ${grey[300]}`,
              bgcolor: grey[50],
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: grey[600], fontSize: "0.75rem", fontWeight: 500 }}
            >
              Not answered
            </Typography>
          </Box>
        </Box>
      );
    }

    // Show submitted state (either just submitted this session OR loaded from history)
    if (isSubmitted || hasExistingSelection) {
      // This session's click wins over the part-text annotation.
      const displayValue = resolveOutcome({
        local: submittedSelection,
        partText: existingSelection,
      })?.label;

      return (
        <Box
          sx={{
            mt: 0,
            mb: 1,
            borderRadius: "0 0 6px 6px",
            border: `1px solid ${grey[300]}`,
            borderTop: "none",
            bgcolor: grey[100],
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.6,
              borderTop: `1px solid ${grey[300]}`,
              bgcolor: grey[50],
            }}
          >
            <CheckIcon sx={{ fontSize: 14, color: green[500] }} />
            <Typography
              variant="caption"
              sx={{
                color: grey[700],
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              {displayValue}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          mt: 0,
          mb: 1,
          bgcolor: grey[50],
          border: `1px solid ${grey[300]}`,
          borderTop: "none",
          borderRadius: "0 0 6px 6px",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: grey[50],
            borderTop: `1px solid ${grey[300]}`,
            borderBottom: `1px solid ${grey[200]}`,
          }}
        >
          <HelpOutlineIcon sx={{ fontSize: 18, color: grey[600] }} />
          <Typography
            variant="body2"
            sx={{ flex: 1, fontWeight: 600, color: grey[800] }}
          >
            Input Required
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2 }}>
          <PromptMarkdown
            text={promptData.question}
            sx={{ mb: 2, color: grey[700], fontWeight: 500 }}
          />

          {/* Buttons component — auto-submits on click (standard button UX) */}
          {promptData.component_type === "buttons" && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {promptData.options.map((option) => (
                <Button
                  key={option.value}
                  variant={
                    selectedValue === option.value ? "contained" : "outlined"
                  }
                  onClick={() => {
                    setSelectedValue(option.value);
                    handleSubmit(option.value, option.label);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  {option.label}
                </Button>
              ))}
              <Button
                variant={
                  selectedValue === "__none__" ? "contained" : "outlined"
                }
                onClick={() => {
                  setSelectedValue("__none__");
                  handleSubmit("none", "None of these");
                }}
                color="inherit"
                sx={{ textTransform: "none", color: grey[600] }}
              >
                None of these
              </Button>
              {promptData.allow_custom && (
                <Button
                  variant={isCustomMode ? "contained" : "outlined"}
                  onClick={handleCustomToggle}
                  color="secondary"
                  sx={{ textTransform: "none" }}
                >
                  Other...
                </Button>
              )}
            </Box>
          )}

          {/* Radio component */}
          {promptData.component_type === "radio" && (
            <FormControl component="fieldset">
              <RadioGroup
                value={isCustomMode ? "__custom__" : selectedValue}
                onChange={(e) => {
                  if (e.target.value === "__custom__") {
                    handleCustomToggle();
                  } else {
                    handleSingleSelect(e.target.value);
                  }
                }}
              >
                {promptData.options.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">{option.label}</Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
                <FormControlLabel
                  value="__none__"
                  control={<Radio />}
                  label={
                    <Typography variant="body2" sx={{ color: grey[600] }}>
                      None of these
                    </Typography>
                  }
                />
                {promptData.allow_custom && (
                  <FormControlLabel
                    value="__custom__"
                    control={<Radio />}
                    label="Other (specify)"
                  />
                )}
              </RadioGroup>
            </FormControl>
          )}

          {/* Dropdown component */}
          {promptData.component_type === "dropdown" && (
            <FormControl fullWidth size="small">
              <Select
                value={isCustomMode ? "__custom__" : selectedValue}
                onChange={(e) => {
                  if (e.target.value === "__custom__") {
                    handleCustomToggle();
                  } else {
                    handleSingleSelect(e.target.value as string);
                  }
                }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select an option...
                </MenuItem>
                {promptData.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      {option.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
                <MenuItem value="__none__">
                  <Typography variant="body2" sx={{ color: grey[600] }}>
                    None of these
                  </Typography>
                </MenuItem>
                {promptData.allow_custom && (
                  <MenuItem value="__custom__">Other (specify)</MenuItem>
                )}
              </Select>
            </FormControl>
          )}

          {/* Checkbox component */}
          {promptData.component_type === "checkbox" && (
            <FormControl component="fieldset">
              <FormGroup>
                {promptData.options.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={selectedValues.includes(option.value)}
                        onChange={() => handleCheckboxChange(option.value)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{option.label}</Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
              {promptData.allow_custom && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCustomMode}
                      onChange={(e) => setIsCustomMode(e.target.checked)}
                    />
                  }
                  label="Other (specify)"
                />
              )}
              <FormHelperText>Select all that apply</FormHelperText>
            </FormControl>
          )}

          {/* Custom input field */}
          {isCustomMode && (
            <TextField
              fullWidth
              size="small"
              placeholder="Type your response..."
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              sx={{ mt: 2 }}
              autoFocus
            />
          )}

          {/* Submit button */}
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              variant="contained"
              onClick={() => handleSubmit()}
              disabled={!canSubmit}
              data-attr="assistant:btn:submit-user-selection"
              sx={{
                textTransform: "none",
                bgcolor: grey[800],
                fontSize: "0.813rem",
                fontWeight: 500,
                px: 2,
                "&:hover": {
                  bgcolor: grey[900],
                },
              }}
            >
              Continue
            </Button>
          </Box>
        </Box>
      </Box>
    );
  },
);
UserSelectionPrompt.displayName = "UserSelectionPrompt";

export default UserSelectionPrompt;
